import Big from "big.js";

import { iterateEntries } from "../../../Common/IterableUtils";
import {
    CalculationFunction,
    CalculationFunctionExecutionResult,
    CalculationResult,
} from "../Engine/Core/AutoCalculations/CalculationResources";
import { ArgMappingType, IOperandsGetter, OperandGettingResult } from "../Engine/Core/AutoCalculations/OperandsGetter";
import { ICalculationFunctionCallContext } from "../Engine/Form/CustomizationContexts/CalculatorContext/CalculationFunctionCallContext";
import { RequisitesInfo } from "../Engine/Form/IForm";
import { FormSettings } from "../Forms/Types/FormSettings";

type ArgMapping = [string] | [string, string] | [string, string, undefined | ArgMappingType];

interface ArgsMapping {
    [name: string]: ArgMapping;
}

interface ArgMappingResult {
    [name: string]: OperandGettingResult;
}

export function getOperands(argsMapping: ArgsMapping, operandsGetter: IOperandsGetter): ArgMappingResult {
    const args: ArgMappingResult = {};

    for (const [argumentName, argumentMapping] of iterateEntries(argsMapping)) {
        if (argumentMapping.length === 1) {
            args[argumentName] = operandsGetter.get(argumentMapping[0]);
        } else if (argumentMapping.length === 2) {
            args[argumentName] = operandsGetter.get(argumentMapping[0], argumentMapping[1]);
        } else if (argumentMapping.length === 3) {
            args[argumentName] = operandsGetter.get(argumentMapping[0], argumentMapping[1], argumentMapping[2]);
        } else {
            throw new Error(`Invalid argument request: ${JSON.stringify(argumentMapping)}`);
        }
    }

    return args;
}

type AsyncCalculationFunction = (
    argsMapping: ArgMappingResult,
    operandsGetter: IOperandsGetter,
    set: FormSettings,
    requisitesInfo: RequisitesInfo
) => CalculationResult[];

export function getAsyncResultHandler(argsMapping: ArgsMapping, calcFn: AsyncCalculationFunction): CalculationFunction {
    return function resultHandler(
        this: ICalculationFunctionCallContext,
        operandsGetter: IOperandsGetter
    ): CalculationFunctionExecutionResult {
        const settings = this.settings;
        const requisites = this.requisites;

        const args = getOperands(argsMapping, operandsGetter);
        const result = calcFn(args, operandsGetter, settings, requisites);

        for (const calculationResult of result) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            if (isNaN(calculationResult[2])) {
                calculationResult[2] = "";
            } else if (calculationResult[2] instanceof Big) {
                calculationResult[2] = calculationResult[2].toFixed();
            } else {
                calculationResult[2] = String(calculationResult[2]);
            }
        }

        return result;
    };
}

type AsyncTextCalculationFunction = (
    argsMapping: ArgMappingResult,
    set: FormSettings,
    requi: RequisitesInfo,
    operandsGetter: IOperandsGetter
) => CalculationResult[];

export function getAsyncTextResultHandler(
    argsMapping: ArgsMapping,
    calcFn: AsyncTextCalculationFunction
): CalculationFunction {
    return function textResultHandler(
        this: ICalculationFunctionCallContext,
        operandsGetter: IOperandsGetter
    ): CalculationFunctionExecutionResult {
        const settings = this.settings;
        const requisites = this.requisites;

        const args = getOperands(argsMapping, operandsGetter);
        const result = calcFn(args, settings, requisites, operandsGetter);

        for (const calculationResult of result) {
            if (!calculationResult[2]) {
                calculationResult[2] = "";
            } else if (calculationResult[2] instanceof Big) {
                calculationResult[2] = calculationResult[2].toFixed();
            } else {
                if (typeof calculationResult[2] === "object") {
                    continue;
                }
                calculationResult[2] = String(calculationResult[2]);
            }
        }

        return result;
    };
}

type AsyncBoolCalculationFunction = (
    argsMapping: ArgMappingResult,
    set: FormSettings,
    operandsGetter: IOperandsGetter,
    context: ICalculationFunctionCallContext
) => CalculationResult[];

export function getAsyncBoolResultHandler(
    argsMapping: ArgsMapping,
    calcFn: AsyncBoolCalculationFunction
): CalculationFunction {
    return function boolResultHandler(
        this: ICalculationFunctionCallContext,
        operandsGetter: IOperandsGetter
    ): CalculationFunctionExecutionResult {
        const settings = this.settings;

        const args = getOperands(argsMapping, operandsGetter);
        return calcFn(args, settings, operandsGetter, this);
    };
}

type AsyncErrorCalculationFunction = (
    argsMapping: ArgMappingResult,
    set: FormSettings,
    rq: RequisitesInfo,
    operandsGetter: IOperandsGetter
) => CalculationResult;

export function getAsyncErrorResultHandler(
    argsMapping: ArgsMapping,
    calcFn: AsyncErrorCalculationFunction
): CalculationFunction {
    return function errorResultHandler(
        this: ICalculationFunctionCallContext,
        operandsGetter: IOperandsGetter
    ): CalculationFunctionExecutionResult {
        const settings = this.settings;
        const requisites = this.requisites;

        const args = getOperands(argsMapping, operandsGetter);
        return calcFn(args, settings, requisites, operandsGetter);
    };
}

export function wrapDecimalOperand(value: Big | number | string | undefined | null): Big {
    if (typeof value === "number" && isFinite(value)) {
        return new Big(value);
    }
    if (typeof value === "string" && /^-?\d+[.]?\d*$/.test(value)) {
        return new Big(value);
    }
    if (value instanceof Big) {
        return value;
    }
    return new Big(0);
}

export function wrapDecimalOrUndefined(value: Big | number | string | undefined | null): Big | undefined {
    if (typeof value === "number" && isFinite(value)) {
        return new Big(value);
    }
    if (typeof value === "string" && /^-?\d+[.]?\d*$/.test(value)) {
        return new Big(value);
    }
    if (value instanceof Big) {
        return value;
    }
    return undefined;
}

export function getSum(values: Array<number | string | undefined | null>): "" | Big {
    let allValuesIsEmpty = true;

    if (values.length === 0) {
        return "";
    }
    let sum = new Big(0);

    for (const val of values) {
        if (val) {
            allValuesIsEmpty = false;
            sum = sum.plus(wrapDecimalOperand(val));
        }
    }
    if (allValuesIsEmpty) {
        return "";
    }

    return sum;
}

export function checkValuesArguments(args: { [name: string]: OperandGettingResult }): boolean {
    for (const argName in args) {
        if (!args.hasOwnProperty(argName)) {
            continue;
        }
        // @ts-ignore
        if (args[argName] && args[argName].values && args[argName].values.find(val => val !== "")) {
            return true;
        }
        // @ts-ignore
        if (args[argName] && !args[argName].values && args[argName] !== "") {
            return true;
        }
    }

    return false;
}

export const getAsyncReturnedResultHandler = getAsyncErrorResultHandler;
